<script setup lang="ts">
import { useTemplateRef } from 'vue';
import { onClickOutside } from '@vueuse/core';

import {
  useCoreStore,
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { PeekPriceButton } from '@apps/main/new-desktop/modules/peekPriceModal';

import {
  AppRawButton,
  AppRouterLink,
  AppStars,
  AppSwiperGallery,
  AppIconButton,
} from '@libs/ui';

import type { HotelMapCardData, HotelMapCardType } from '../models';

interface HotelMapCardProps {
  data: HotelMapCardData;
  cardType: HotelMapCardType;
}

const props = defineProps<HotelMapCardProps>();

interface HotelMapCardEmits {
  (eventName: 'click'): void;
  (eventName: 'hide'): void;
  (eventName: 'open-peek-price-calendar'): void;
}

const emit = defineEmits<HotelMapCardEmits>();

const coreStore = useCoreStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();
const itineraryStore = useItineraryStore();

const hotelMapCard = useTemplateRef('hotelMapCard');

onClickOutside(hotelMapCard, () => emit('hide'));

// NOTE: This could be false when all days in month are cached in a single date search session. Then opening peek price calendar for a different hotel than the one included in the itinerary would result in Peek Price with not-searched dates.
const isSingleDateSearch = !itineraryStore.areWholeMonthItinerariesSearched;

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionMapCardClicked({
    mapSize: props.cardType,
    hotelCode: props.data.hotelCode,
  });
}
</script>

<template>
  <div
    ref="hotelMapCard"
    class="hotel-map-card-wrapper"
    :class="{ [props.cardType]: true }"
  >
    <AppIconButton
      class="close-button"
      type="secondary"
      state="enabled"
      size="small"
      :icon="{ name: 'close' }"
      :ariaLabel="$t('a11y.close_image_gallery')"
      @click="emit('hide')"
    />
    <AppRawButton class="hotel-map-card" @click="emit('click')">
      <div class="image-wrapper">
        <AppSwiperGallery
          :image-urls="props.data.imageUrls"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'hotel-selection-map-card',
              })
          "
          slides-amount-position="right"
          slides-amount-size="s"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
        />
      </div>
      <div class="hotel-base-info">
        <AppRouterLink
          :link="
            routerStore.getHotelDetailsUrlInHotelSelection(
              { ingredientIndex: props.data.ingredientIndex },
              props.data.hotelCode
            )
          "
          font="font-label-s-bold"
          color="color-neutral-70"
          @navigation-triggered="onNavigationTriggered"
        >
          {{ props.data.hotelName }}
        </AppRouterLink>
        <div class="ratings">
          <AppStars class="stars" :amount="props.data.starsRating" size="18" />
          <CustomersRatingTag
            v-if="props.data.guestRating"
            :guest-rating="props.data.guestRating"
            font-size="s"
          />
        </div>
      </div>
    </AppRawButton>
    <div class="peek-price-calendar">
      <PeekPriceButton
        v-if="!isSingleDateSearch"
        :wide="true"
        @click="emit('open-peek-price-calendar')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-map-card-wrapper {
  position: relative;

  box-shadow: var(--shadow-elevation-200);
  background-color: var(--color-white);
  border-radius: var(--border-radius-8);

  display: flex;
  flex-flow: column;

  &.mobile {
    width: clamp(256px, 100vw - var(--spacing-24), 450px);
    .hotel-map-card {
      .image-wrapper {
        height: 20vh;
      }
    }
  }

  &.desktopCollapsed {
    width: 300px;
    .hotel-map-card {
      .image-wrapper {
        height: 150px;
      }
    }
  }

  &.desktopExtended {
    width: 330px;
    .hotel-map-card {
      .image-wrapper {
        height: 200px;
      }
    }
  }

  .close-button {
    position: absolute;
    top: var(--spacing-8);
    right: var(--spacing-8);
    z-index: 1;
  }

  @include on-hover {
    cursor: pointer;
    background: var(--color-neutral-10);
  }

  .hotel-map-card {
    display: flex;
    flex-direction: column;
    width: 100%;
    .image-wrapper {
      flex-grow: 1;
      overflow: hidden;
      width: 100%;
      border-top-left-radius: var(--border-radius-8);
      border-top-right-radius: var(--border-radius-8);
    }

    .hotel-base-info {
      padding: var(--spacing-12);
      flex-flow: column;
      display: flex;
      gap: var(--spacing-4);

      .ratings {
        display: flex;
        gap: var(--spacing-12);
        padding-top: var(--spacing-4);
        align-items: center;
      }
    }
  }

  .peek-price-calendar {
    padding: 0 var(--spacing-12) var(--spacing-12);
    display: flex;
  }
}
</style>
